import { useBPLogger } from '@/composables/business-process-logger';
import { type OrdersParams } from '@/models/orders-params';
import { ValidatorResult } from '@/models/validation-result.model';
import {
  ManuallyDeletableOrderDto,
  ManuallyRetriableOrderDto,
  OrderCopyAccountResultDto,
  OrderCopyOrderResultDto,
  OrderCopyRequestDto,
  OrderHeaderModel,
  OrderSummaryDto,
  OrderUpdateDto,
} from '@onrunning/backstage-models';
import { Order } from '../models/order.model';
import { useOrderHeadersStore } from '../stores/order-headers';
import { CrudService } from './crud.service';

export class OrdersService {
  private crudService: CrudService<Order>;
  private readonly setRefreshHeaders: () => void;
  private submitOrderBPLog;

  constructor() {
    const { setRefreshHeaders } = useOrderHeadersStore();
    this.setRefreshHeaders = setRefreshHeaders;
    this.crudService = new CrudService<Order>('api/v2/orders');
    const { submitOrderBPLog } = useBPLogger();
    this.submitOrderBPLog = submitOrderBPLog;
  }

  public get(id: string, accountId: string, withRelatedOrders = false): Promise<ValidatorResult<Order>> {
    return this.crudService.get(id, { params: { accountId, withRelatedOrders } });
  }

  public downloadOrder(id: string, accountId: string, isDraft: boolean) {
    const queryParams = new URLSearchParams();

    queryParams.set('accountId', accountId);
    queryParams.set('isDraft', isDraft.toString());
    const headers = {
      'Content-Disposition': `attachment; filename=order-${id}.xlsx`,
      'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64',
    };

    return this.crudService.do('get', `${id}/download-xls?${queryParams}`, {
      headers,
      responseType: 'arraybuffer',
    });
  }

  public getOrderHeaders(params: OrdersParams): Promise<ValidatorResult<OrderHeaderModel[]>> {
    const queryParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      queryParams.set(key, value);
    }
    const { refreshHeaders } = useOrderHeadersStore();

    queryParams.set('refresh', refreshHeaders);

    return this.crudService.do('get', `headers?${queryParams}`);
  }

  public getOrderSummary(accountId: string, orderNumbers: string[]): Promise<ValidatorResult<OrderSummaryDto[]>> {
    const queryParams = new URLSearchParams();

    queryParams.set('accountId', accountId);

    return this.crudService.do('post', `summary?${queryParams}`, { orderNumbers });
  }

  public getOrderTypes(): Promise<ValidatorResult<OrderHeaderModel[]>> {
    return this.crudService.do('get', 'types');
  }

  public async updateOrder(orderUpdate: OrderUpdateDto, accountId: string): Promise<ValidatorResult<OrderUpdateDto>> {
    const result = await this.crudService.do('put', `${orderUpdate.orderNumber}?accountId=${accountId}`, orderUpdate);

    const logData = {
      order_number: result?.model?.orderNumber,
      subflow: 'order_duplication',
    };
    if (result.isValid()) {
      console.log(this.submitOrderBPLog(true, logData));
    } else {
      const error_message = result?.errors?.join(',');
      console.log(
        this.submitOrderBPLog(false, {
          ...logData,
          error_message,
        }),
      );
    }

    this.setRefreshHeaders();

    return result;
  }

  public async retryOrders(orderNumbers: string[]) {
    const result = await this.crudService.do('post', 'retry', { orderNumbers });

    this.setRefreshHeaders();

    return result;
  }

  public async duplicateOrders(
    payload: OrderCopyRequestDto,
    accountId: string,
  ): Promise<ValidatorResult<OrderCopyAccountResultDto[]>> {
    const queryParams = new URLSearchParams();
    queryParams.set('accountId', accountId);

    const result = await this.crudService.do('post', `copy?${queryParams}`, payload);

    const orderNumbers = result?.model?.orders.map((order: OrderCopyOrderResultDto) => order.orderNumber);
    const logData = {
      order_numbers: orderNumbers,
      subflow: 'order_duplication',
    };

    if (result.isValid()) {
      console.log(this.submitOrderBPLog(true, logData));
    } else {
      const error_message = result?.errors?.join(',');
      console.log(this.submitOrderBPLog(false, { ...logData, error_message }));
    }

    this.setRefreshHeaders();

    return result;
  }

  public async validateDuplicateOrders(
    payload: OrderCopyRequestDto,
    accountId: string,
  ): Promise<ValidatorResult<OrderCopyAccountResultDto[]>> {
    const queryParams = new URLSearchParams();
    queryParams.set('accountId', accountId);

    const result = await this.crudService.do('post', `copy/validate?${queryParams}`, payload);

    this.setRefreshHeaders();

    return result;
  }

  public deleteOrders(orderNumbers: string[]) {
    return this.crudService.do('post', 'delete', { orderNumbers });
  }

  public getRetriableOrders(): Promise<ValidatorResult<ManuallyRetriableOrderDto[]>> {
    return this.crudService.do('get', 'get-manually-retriable');
  }
  public getDeletableOrders(): Promise<ValidatorResult<ManuallyDeletableOrderDto[]>> {
    return this.crudService.do('get', 'manually-deletable');
  }
}
